import React, { useState, useRef } from 'react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';

const QR_PNG_SIZE = 800;

// Split text into lines that fit `maxWidth` on the canvas.
const wrapLines = (ctx, text, maxWidth) => {
  const lines = [];
  let line = '';
  for (const word of text.split(' ')) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  return lines;
};

/**
 * A QR code card for a public link (attendance, feedback): shown on screen,
 * full screen for the projector, as a PNG to send to a trainer, or as a link.
 *
 *   title / subtitle  - printed above the code in the PNG and full-screen view
 *   instruction       - one line telling people what scanning does
 *   fileName          - PNG name without extension
 *   fullScreenFooter  - optional extra content under the full-screen code (e.g. a live count)
 */
const QrPanel = ({ link, title, subtitle, instruction, fileName, fullScreenFooter }) => {
  const [fullScreen, setFullScreen] = useState(false);
  const canvasRef = useRef(null);

  const copyLink = () => {
    navigator.clipboard?.writeText(link);
    alert('Link copied');
  };

  // A ready-to-share picture: title, subtitle, QR code and a one-line
  // instruction, so a trainer can put it on the projector without logging in.
  const downloadQr = () => {
    const qr = canvasRef.current?.querySelector('canvas');
    if (!qr) return;
    const width = QR_PNG_SIZE + 160;
    const ctx0 = document.createElement('canvas').getContext('2d');
    ctx0.font = 'bold 44px Arial, sans-serif';
    const titleLines = wrapLines(ctx0, title, width - 80);
    const top = 70 + titleLines.length * 56 + (subtitle ? 50 : 10);
    const height = top + QR_PNG_SIZE + 110;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.textAlign = 'center';
    ctx.fillStyle = '#111827';
    ctx.font = 'bold 44px Arial, sans-serif';
    titleLines.forEach((l, i) => ctx.fillText(l, width / 2, 80 + i * 56));
    if (subtitle) {
      ctx.fillStyle = '#6b7280';
      ctx.font = '28px Arial, sans-serif';
      ctx.fillText(subtitle, width / 2, 70 + titleLines.length * 56 + 20);
    }
    ctx.drawImage(qr, 80, top, QR_PNG_SIZE, QR_PNG_SIZE);
    ctx.fillStyle = '#111827';
    ctx.font = '30px Arial, sans-serif';
    ctx.fillText(instruction, width / 2, top + QR_PNG_SIZE + 60);

    canvas.toBlob((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName.replace(/[^a-zA-Z0-9]+/g, '_')}.png`;
      a.click();
      window.URL.revokeObjectURL(url);
    }, 'image/png');
  };

  return (
    <div className="card text-center">
      <QRCodeSVG value={link} size={220} includeMargin />
      <p className="text-sm text-muted" style={{ wordBreak: 'break-all', margin: '8px 0 12px' }}>{link}</p>
      <div className="flex gap-2" style={{ justifyContent: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={() => setFullScreen(true)}>Show full screen</button>
        <button className="btn btn-secondary" onClick={downloadQr}>Download QR image</button>
        <button className="btn btn-secondary" onClick={copyLink}>Copy link</button>
      </div>
      {/* High-resolution copy for the PNG download; never shown. */}
      <div ref={canvasRef} style={{ display: 'none' }}>
        <QRCodeCanvas value={link} size={QR_PNG_SIZE} includeMargin />
      </div>

      {/* Projector view */}
      {fullScreen && (
        <div onClick={() => setFullScreen(false)} style={{
          position: 'fixed', inset: 0, background: 'white', zIndex: 1000,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', padding: '24px', textAlign: 'center'
        }}>
          <h1 style={{ fontSize: '36px', marginBottom: '8px' }}>{title}</h1>
          <p style={{ fontSize: '20px', color: '#6b7280', marginBottom: '24px' }}>{instruction}</p>
          <QRCodeSVG value={link} size={Math.min(window.innerWidth, window.innerHeight) * 0.6} includeMargin />
          {fullScreenFooter}
          <p className="text-muted text-sm">Click anywhere to close</p>
        </div>
      )}
    </div>
  );
};

export default QrPanel;
